import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateWorkspaceSecretDescription } from '@/lib/api/generated/api';
import type { WorkspaceSecretView } from '@/lib/api/generated/model';

import { toSecretError } from '../errors';
import { secretKeys } from '../queryKeys';

/**
 * Change one entry's description, and nothing else.
 *
 * Separate from {@link useReplaceWorkspaceSecretValue} because the two are
 * separate acts with different consequences: this is a correction, that one is
 * irreversible for a secret. The request carries no value — it cannot, and that
 * is the point. A secret's value is unreadable, so a path that required one
 * before it would accept a typo fix is a path that refuses to fix typos.
 *
 * `null` clears the description. The service treats an empty or whitespace-only
 * string the same way, so a cleared field and a never-filled one are
 * indistinguishable afterwards.
 */
export function useUpdateSecretDescription(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      key,
      description,
    }: {
      key: string;
      description: string | null;
    }): Promise<WorkspaceSecretView> => {
      const response = await updateWorkspaceSecretDescription(
        workspaceId,
        key,
        { description },
      );
      if (response.status === 200) return response.data;
      throw toSecretError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: secretKeys.all(workspaceId),
      });
    },
  });
}
